import type { Client, Log } from 'viem'

import {
  type Address,
  type ChainId,
  getChainAddresses,
  type MarketId,
  MathLib
} from '@morpho-org/blue-sdk'
import { blueAbi } from '@morpho-org/morpho-sdk/abis'
import { isDefined } from '@morpho-org/morpho-ts'
import { isAddressEqual, parseEventLogs } from 'viem'
import { getCode } from 'viem/actions'

import type { SimulationStatus } from '@/services/simulate/simulatedPosition.helpers'

import { getCollateralValue } from '../marketMaxLtv'

/** Debounce window (ms) before an edited amount drives the local build (validation only). */
export const PREVIEW_DEBOUNCE_MS = 400

/**
 * Deadline (unix seconds) stamped into every morpho-sdk v6 market write. v6 requires an
 * explicit `deadline` on the entity methods — BlueBundlesV1 reverts past it, and it is baked
 * into the Permit2 / `setAuthorization` signatures `getRequirements()` returns. The action is
 * built once inside the builder's prepared query and reused for both the review simulation and
 * submit, so a single wall-clock stamp stays consistent across that pair. 30 min sits well
 * inside the SDK's 2h forward-accrual quote horizon (the hard clamp for shares-mode repay)
 * while bounding stale execution and the wrapped-then-refunded native buffer on a full close.
 */
export const MARKET_ACTION_DEADLINE_SECONDS = 30n * 60n

export const getMarketActionDeadline = (): bigint =>
  BigInt(Math.floor(Date.now() / 1000)) + MARKET_ACTION_DEADLINE_SECONDS

/**
 * Rebuild cadence for the prepared market-action queries. The stamped `deadline` is baked into
 * the built calldata and signatures, so a cached prepared action must rebuild 5 min before the
 * 30-min deadline expires instead of sitting "ready" past it.
 */
export const MARKET_ACTION_REBUILD_INTERVAL_MS =
  Number(MARKET_ACTION_DEADLINE_SECONDS - 5n * 60n) * 1000

/** Submits within this margin of the stamped deadline are rebuilt instead of sent. */
export const MARKET_ACTION_DEADLINE_MARGIN_SECONDS = 60n

export const isMarketActionDeadlineExpired = (
  deadline: bigint | undefined,
  nowMs = Date.now()
): boolean =>
  deadline !== undefined &&
  deadline <= BigInt(Math.floor(nowMs / 1000)) + MARKET_ACTION_DEADLINE_MARGIN_SECONDS

/**
 * Runs `submit` only if the stamped `deadline` is still safely in the future; otherwise
 * triggers `rebuild` so the dialog re-prepares the bundle instead of submitting an expired one.
 * Returns whether `submit` ran.
 */
export const submitUnlessDeadlineExpired = ({
  deadline,
  rebuild,
  submit
}: {
  deadline: bigint | undefined
  rebuild: () => unknown
  submit: () => void
}): boolean => {
  if (isMarketActionDeadlineExpired(deadline)) {
    void rebuild()
    return false
  }
  submit()
  return true
}

export class BlueBundlesV1NotDeployedError extends Error {
  constructor(
    public readonly chainId: ChainId,
    public readonly address: Address
  ) {
    super(`BlueBundlesV1 has no code at ${address} on chain ${chainId}`)
    this.name = 'BlueBundlesV1NotDeployedError'
  }
}

const blueBundlesV1CodeChecks = new Map<ChainId, Promise<void>>()

/** Resolves once BlueBundlesV1 is confirmed deployed on `chainId`; throws BlueBundlesV1NotDeployedError otherwise. Cached per chain (a deployment never disappears); a failed RPC read is not cached. */
export function assertBlueBundlesV1Deployed(client: Client, chainId: ChainId): Promise<void> {
  const address = getChainAddresses(chainId).bundles?.blueBundlesV1
  if (!address) return Promise.resolve()

  const cached = blueBundlesV1CodeChecks.get(chainId)
  if (cached) return cached

  const check = getCode(client, { address })
    .then(code => {
      if (code === undefined || code === '0x') {
        blueBundlesV1CodeChecks.delete(chainId)
        throw new BlueBundlesV1NotDeployedError(chainId, address)
      }
    })
    .catch(error => {
      blueBundlesV1CodeChecks.delete(chainId)
      throw error
    })
  blueBundlesV1CodeChecks.set(chainId, check)
  return check
}

/**
 * Which `client.morpho.blue(...)` method a panel input pair maps to.
 *
 * The Simple Action Panel / Position Manager expose two inputs per mode:
 * - `supplyBorrow` mode: collateral-to-supply + loan-to-borrow
 * - `repayWithdraw` mode: loan-to-repay + collateral-to-withdraw
 * - `lend` mode: a single loan-to-lend input (opens/tops up the lend position)
 * - `lendWithdraw` mode: a single loan-to-withdraw input (exits the lend position)
 *
 * `none` means both inputs are empty (nothing to build).
 */
export type MarketActionKind =
  | 'supply'
  | 'supplyCollateral'
  | 'borrow'
  | 'supplyCollateralBorrow'
  | 'repay'
  | 'withdrawCollateral'
  | 'repayWithdrawCollateral'
  | 'withdraw'
  | 'none'

/** The input surfaces feeding {@link resolveMarketActionKind}. */
export type MarketActionMode = 'supplyBorrow' | 'repayWithdraw' | 'lend' | 'lendWithdraw'

/** Shared-liquidity calldata is safe to submit only after the exact bundle simulates successfully. */
export const isSharedLiquiditySimulationUnverified = ({
  hasReallocations,
  status
}: {
  hasReallocations: boolean
  status: SimulationStatus
}): boolean => hasReallocations && status !== 'success'

/**
 * Routes the two panel inputs to the atomic morpho-sdk `blue` method (WS2).
 *
 * @param params.collateralAmount - supply-collateral amount (supplyBorrow mode)
 *   or withdraw-collateral amount (repayWithdraw mode); always 0 in the lend modes.
 * @param params.loanAmount - borrow amount (supplyBorrow mode), repay amount
 *   (repayWithdraw mode), loan amount to lend (lend mode) or loan withdrawal
 *   amount (lendWithdraw mode).
 * @param params.mode - which panel the inputs come from.
 */
export const resolveMarketActionKind = ({
  collateralAmount,
  loanAmount,
  mode
}: {
  collateralAmount: bigint
  loanAmount: bigint
  mode: MarketActionMode
}): MarketActionKind => {
  const hasCollateral = collateralAmount > 0n
  const hasLoan = loanAmount > 0n

  if (mode === 'lend') {
    return hasLoan ? 'supply' : 'none'
  }

  if (mode === 'lendWithdraw') {
    return hasLoan ? 'withdraw' : 'none'
  }

  if (mode === 'supplyBorrow') {
    if (hasCollateral && hasLoan) return 'supplyCollateralBorrow'
    if (hasCollateral) return 'supplyCollateral'
    if (hasLoan) return 'borrow'
    return 'none'
  }

  if (hasLoan && hasCollateral) return 'repayWithdrawCollateral'
  if (hasLoan) return 'repay'
  if (hasCollateral) return 'withdrawCollateral'
  return 'none'
}

/** Minimal `AccrualPosition` shape the simulation signature reads (collateral/debt/supply + market accrual state). */
interface SimulationKeyPosition {
  collateral: bigint
  borrowShares: bigint
  supplyShares: bigint
  market: {
    totalBorrowAssets: bigint
    totalBorrowShares: bigint
    totalSupplyAssets: bigint
    totalSupplyShares: bigint
    lastUpdate: bigint
    price?: bigint
  }
}

/**
 * Minimal `VaultV2BlueReallocation` shape the simulation signature reads. v6 BluePublicAllocator
 * reallocations carry a loan-token `penalty` (WAD rate) and `assets` in place of the V1 native
 * `fee`/`withdrawals`; the signature keys on the fields that shape the signed calldata.
 */
interface SimulationKeyReallocation {
  vault: Address
  assets: bigint
  penalty: bigint
}

/**
 * Stable content signature of a built market action — every input that shapes the signed calldata
 * (kind, amounts, exact-shares modes, position accrual state, reallocations).
 *
 * The review modal keys its evm-simulation by this, NOT by raw calldata: morpho-sdk stamps a
 * wall-clock `metadata.timestamp` byte into every `buildTx()`, so keying on calldata would churn
 * the key every poll and flash the recap skeleton. Computed inside the builder's prepared query,
 * atomic with the `action` it describes, so the sim key and the submitted tx advance together
 * rather than racing — otherwise the recap could verify one bundle while Confirm submits another.
 *
 * `undefined`/null position ⇒ `'no-position'` (collateral-only supply needs no position).
 */
export const buildMarketActionSimulationKey = ({
  kind,
  collateralAmount,
  loanAmount,
  nativeAmount,
  isMaxRepay,
  shouldUseSupplyShares = false,
  positionData,
  reallocations
}: {
  kind: MarketActionKind
  collateralAmount: bigint
  loanAmount: bigint
  nativeAmount: bigint
  isMaxRepay: boolean
  /** Whether lend-withdraw should burn exact supply shares; defaults to false. */
  shouldUseSupplyShares?: boolean
  positionData: SimulationKeyPosition | undefined | null
  reallocations: readonly SimulationKeyReallocation[] | undefined
}): string => {
  const positionSignature = positionData
    ? [
        positionData.collateral,
        positionData.borrowShares,
        positionData.supplyShares,
        positionData.market.totalBorrowAssets,
        positionData.market.totalBorrowShares,
        positionData.market.totalSupplyAssets,
        positionData.market.totalSupplyShares,
        positionData.market.lastUpdate,
        positionData.market.price ?? 0n
      ]
        .map(String)
        .join(':')
    : 'no-position'

  const reallocationsSignature = (reallocations ?? [])
    .map(({ vault, assets, penalty }) => `${vault}:${assets}:${penalty}`)
    .join('|')

  return [
    kind,
    collateralAmount.toString(),
    loanAmount.toString(),
    nativeAmount.toString(),
    String(isMaxRepay),
    String(shouldUseSupplyShares),
    positionSignature,
    reallocationsSignature
  ].join('|')
}

/**
 * Repay/close amount, in the morpho-sdk v6 repay shape (S6).
 *
 * A "max"/close repay burns the exact reviewed `positionData.borrowShares` — shares are
 * accrual-immune, and the user signs exactly what they reviewed: a saturating `maxUint256`
 * would repay the live debt the review never showed and revert once it exceeds the funded
 * `maxRepayAssets`. A partial repay uses the entered asset amount (`{ repayAssets }`).
 *
 * morpho-sdk v6 native funding is EXCLUSIVE (all-or-nothing): `nativeAmount` is either 0 (plain
 * ERC-20 repay) or the full funded leg. For a partial repay that leg is `repayAssets`; for a max
 * close it is the SDK-derived `maxRepayAssets`, which the caller pre-computes (a dry ERC-20 build)
 * and passes in. The caller owns that value; this helper only shapes the args.
 *
 * @param params.isMaxRepay - whether the user picked "max" / full close.
 * @param params.repayAssets - the entered asset amount (partial repay).
 * @param params.nativeAmount - native (ETH) leg to wrap toward the repay: 0 or the full funded
 *   leg (default 0n).
 */
export const resolveRepayAmount = ({
  isMaxRepay,
  repayAssets,
  borrowShares,
  nativeAmount = 0n
}: {
  isMaxRepay: boolean
  repayAssets: bigint
  borrowShares: bigint
  nativeAmount?: bigint
}): { repayShares: bigint; nativeAmount: bigint } | { repayAssets: bigint; nativeAmount: bigint } =>
  isMaxRepay ? { repayShares: borrowShares, nativeAmount } : { repayAssets, nativeAmount }

/**
 * MAX lend-withdraw mode decision: burn exact supply shares when the full exit fits local
 * market liquidity — shares are accrual-immune, while an assets-mode MAX strands the
 * interest accrued since quote as dust. The reallocation plan is orthogonal and rides in
 * both modes: `canFullExitWithExactShares` guarantees the burn fits local liquidity alone,
 * and a plan only adds liquidity on top. A zero displayed max has no buildable input:
 * assets mode.
 */
export const shouldUseExactSharesForMaxLendWithdraw = ({
  isMaxActive,
  canFullExitWithExactShares,
  maxWithdrawAssets,
  hasReallocations
}: {
  isMaxActive: boolean
  canFullExitWithExactShares: boolean
  maxWithdrawAssets: bigint | undefined
  /** A non-empty allocator plan forces assets mode, per the ADR's withdrawal policy. */
  hasReallocations: boolean
}): boolean =>
  isMaxActive &&
  canFullExitWithExactShares &&
  !hasReallocations &&
  isDefined(maxWithdrawAssets) &&
  maxWithdrawAssets > 0n

/**
 * Splits a user-entered total of the wrapped-native token — collateral to supply, or loan
 * to repay/lend — into the ERC20 portion and the native portion (ETH wrapped 1:1 into wNative
 * inside the bundle).
 *
 * morpho-sdk v6 (BlueBundlesV1) makes native funding EXCLUSIVE: `nativeAmount` must be either
 * 0 or exactly the full funded leg — a partial split throws `NativeFundingAmountMismatchError`
 * (`validateBlueBundlesV1NativeFunding`). So the choice is all-or-nothing: `isWrapping` (the
 * "pay with native ETH" tab) funds the entire amount from native; otherwise the entire amount
 * is pulled from the ERC20 wNative balance. The caller caps the input to the matching balance
 * per tab (native ⇒ wrappable ETH, wrapped ⇒ ERC20 wNative).
 *
 * Only valid when the funded leg's token is the chain's wNative: the morpho-sdk builders throw
 * `NativeAmountOnNonWNativeAssetError` when `nativeAmount > 0` on any other token, so callers
 * gate `isWrapping` on the token being wNative.
 *
 * @param params.totalAmount - the user-entered total of the wrapped-native token.
 * @param params.isWrapping - whether the user chose to pay with native ETH (vs the ERC20 wNative).
 */
export const computeNativeWrapSplit = ({
  totalAmount,
  isWrapping
}: {
  totalAmount: bigint
  isWrapping: boolean
}): { erc20Amount: bigint; nativeAmount: bigint } =>
  isWrapping
    ? { erc20Amount: 0n, nativeAmount: totalAmount }
    : { erc20Amount: totalAmount, nativeAmount: 0n }

/**
 * Thrown when a lend would open the second side of a market the signer already borrows on. Blue
 * allows both, the app does not, and every entry point's gate reads a source that can lag, so
 * the builder re-checks the accrued position it was handed before it encodes anything.
 */
export class MarketOneSidedPositionError extends Error {
  constructor() {
    super('You can only have a position on one side of this market at a time.')
    this.name = 'MarketOneSidedPositionError'
  }
}

/** Thrown when the action would be built/submitted for an address other than the connected signer (S1). */
export class BuilderSignerMismatchError extends Error {
  constructor(
    readonly userAddress: Address | undefined,
    readonly accountAddress: Address | undefined
  ) {
    super(
      `Market action builder/signer mismatch: cannot build for ${userAddress ?? 'undefined'} while connected as ${accountAddress ?? 'undefined'}`
    )
    this.name = 'BuilderSignerMismatchError'
  }
}

/** Thrown when the builder runs against a chain other than the action's market chain (S1). */
export class BuilderChainMismatchError extends Error {
  constructor(
    readonly clientChainId: number | undefined,
    readonly expectedChainId: number
  ) {
    super(
      `Market action builder chain mismatch: client on ${clientChainId ?? 'undefined'}, expected ${expectedChainId}`
    )
    this.name = 'BuilderChainMismatchError'
  }
}

/**
 * Enforces invariant S1 (builder == signer) before any build/submit. The
 * high-level morpho-sdk helpers take a single `userAddress`; this guarantees it
 * is the connected account and that the client is on the market's chain, so the
 * UI can never build a transaction that moves a different address's funds.
 *
 * @throws {BuilderSignerMismatchError} when `userAddress` ≠ the connected account.
 * @throws {BuilderChainMismatchError} when the client chain ≠ the market chain.
 */
export const assertBuilderIsSigner = ({
  userAddress,
  accountAddress,
  clientChainId,
  expectedChainId
}: {
  userAddress: Address | undefined
  accountAddress: Address | undefined
  clientChainId: number | undefined
  expectedChainId: number
}): void => {
  if (!userAddress || !accountAddress || !isAddressEqual(userAddress, accountAddress)) {
    throw new BuilderSignerMismatchError(userAddress, accountAddress)
  }
  if (clientChainId !== expectedChainId) {
    throw new BuilderChainMismatchError(clientChainId, expectedChainId)
  }
}

/**
 * Loan-to-value (WAD) of a position, as morpho-sdk derives it:
 * `borrowAssets / collateralValue`, where `collateralValue` is the oracle-scaled
 * collateral value in loan-asset terms.
 *
 * Mirrors blue-sdk `MarketUtils.getLtv` at the zero-collateral edge: a position
 * carrying debt with no collateral value is infinitely unhealthy, so it returns
 * `MathLib.MAX_UINT_256` (the "> 100%" sentinel) — NOT a misleading safe `0%`, which
 * would render a debt/no-collateral leg green and hide it from the LTV risk gate. A
 * truly empty position (no debt) stays `0`.
 */
export const computeLtv = ({
  collateral,
  borrowAssets,
  price
}: {
  collateral: bigint
  borrowAssets: bigint
  price: bigint
}): bigint => {
  const collateralValue = getCollateralValue(collateral, price)
  if (collateralValue === 0n) return borrowAssets > 0n ? MathLib.MAX_UINT_256 : 0n
  return MathLib.mulDivDown(borrowAssets, MathLib.WAD, collateralValue)
}

/**
 * Quick JS-math preview of the post-action position, shown in the **input
 * surfaces** (Simple Action Panel + Position Manager dialogs) the instant the
 * user types — no network round-trip. The oracle price is held constant (it
 * doesn't move within a single block) and amounts are applied as plain deltas:
 * `supplyBorrow` adds collateral / borrow, `repayWithdraw` subtracts repay /
 * withdraw (zero-floored, since you can't repay/withdraw past zero).
 *
 * This is intentionally a cheap estimate. The authoritative post-action state —
 * which accounts for accrued interest, slippage, and bundle side effects — comes
 * from the review/confirm modal's evm-simulation (`useMarketSimulatedFinalPosition`).
 *
 * @param params.current - current on-chain position + oracle price (from `useMarketBlue`).
 * @param params.collateralDelta - collateral to supply (supplyBorrow) or withdraw (repayWithdraw).
 * @param params.loanDelta - loan to borrow (supplyBorrow) or repay (repayWithdraw).
 * @param params.mode - which input surface the amounts come from.
 */
export const computePreviewFinalPosition = ({
  current,
  collateralDelta,
  loanDelta,
  mode
}: {
  current: { collateral: bigint; borrowAssets: bigint; price: bigint }
  collateralDelta: bigint
  loanDelta: bigint
  mode: 'supplyBorrow' | 'repayWithdraw'
}): { collateral: bigint; loan: bigint; ltv: bigint } => {
  const collateral =
    mode === 'supplyBorrow'
      ? current.collateral + collateralDelta
      : MathLib.zeroFloorSub(current.collateral, collateralDelta)
  const loan =
    mode === 'supplyBorrow'
      ? current.borrowAssets + loanDelta
      : MathLib.zeroFloorSub(current.borrowAssets, loanDelta)
  return {
    collateral,
    loan,
    ltv: computeLtv({ collateral, borrowAssets: loan, price: current.price })
  }
}

/**
 * Whether a receipt really is the lend action it was submitted for.
 *
 * `submitTransactions` reaches its success state through replacement detection too, so a
 * cancelled or same-nonce-replaced transaction resolves with someone else's receipt. Only a Blue
 * event pinned to this market, this signer and this side separates the two, so the emitter is
 * filtered as well: the topic0s are not unique across contracts.
 */
export const isRealisedMarketLendAction = (
  logs: readonly Log[] | undefined,
  {
    morphoAddress,
    marketId,
    userAddress,
    eventName
  }: {
    /** Undefined on a chain with no Morpho deployment, where nothing can be verified. */
    morphoAddress: Address | undefined
    marketId: MarketId
    userAddress: Address | undefined
    eventName: 'Supply' | 'Withdraw'
  }
): boolean =>
  !!logs &&
  !!morphoAddress &&
  !!userAddress &&
  parseEventLogs({
    abi: blueAbi,
    logs: logs.filter(log => isAddressEqual(log.address, morphoAddress)),
    eventName,
    args: { id: marketId, onBehalf: userAddress }
  }).length > 0
